import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import { LocateFixed } from "lucide-react";

const TH_CENTER = [13.7563, 100.5018];

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    const valid = (points || []).filter((p) => Array.isArray(p) && p.length === 2);
    if (valid.length === 1) {
      map.setView(valid[0], 8);
    } else if (valid.length > 1) {
      map.fitBounds(valid, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
};

// Bridge to capture the Leaflet map instance for imperative actions (live location)
const MapReady = ({ onReady }) => {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  return null;
};

// routes: [{ from:[lat,lng], to:[lat,lng], color, fromLabel, toLabel }]
export const RideMap = ({ routes = [], height = 360, center = TH_CENTER, zoom = 6, showLocate = true }) => {
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);

  const allPoints = [];
  routes.forEach((r) => {
    if (r.from) allPoints.push(r.from);
    if (r.to) allPoints.push(r.to);
  });

  const locate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(p);
        if (mapRef.current) mapRef.current.flyTo(p, 12, { duration: 1.2 });
        setLocating(false);
      },
      () => { setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div style={{ height }} className="relative w-full rounded-[var(--radius)] overflow-hidden border border-white/10">
      {showLocate && (
        <button
          type="button"
          onClick={locate}
          data-testid="map-locate-button"
          className="absolute z-[1000] top-3 right-3 glass-solid text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1 hover:glow-cyan transition-shadow"
        >
          <LocateFixed size={14} className={`text-[#22D3EE] ${locating ? "animate-pulse" : ""}`} />
          {locating ? "Locating…" : "My live location"}
        </button>
      )}
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <MapReady onReady={(m) => (mapRef.current = m)} />
        {routes.map((r, i) => (
          <React.Fragment key={i}>
            {r.from && r.to && (
              <Polyline positions={[r.from, r.to]} pathOptions={{ color: r.color || "#22D3EE", weight: 3, opacity: 0.85, dashArray: "6 8" }} />
            )}
            {r.from && (
              <CircleMarker center={r.from} radius={7} pathOptions={{ color: "#7C3AED", fillColor: "#7C3AED", fillOpacity: 0.9 }}>
                {r.fromLabel && <Tooltip>{r.fromLabel}</Tooltip>}
              </CircleMarker>
            )}
            {r.to && (
              <CircleMarker center={r.to} radius={7} pathOptions={{ color: "#F7C948", fillColor: "#F7C948", fillOpacity: 0.9 }}>
                {r.toLabel && <Tooltip>{r.toLabel}</Tooltip>}
              </CircleMarker>
            )}
          </React.Fragment>
        ))}
        {userPos && (
          <>
            <CircleMarker center={userPos} radius={16} pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.15, weight: 1 }} />
            <CircleMarker center={userPos} radius={7} pathOptions={{ color: "#60A5FA", fillColor: "#3B82F6", fillOpacity: 1, weight: 2 }}>
              <Tooltip permanent>You are here</Tooltip>
            </CircleMarker>
          </>
        )}
        <FitBounds points={allPoints} />
      </MapContainer>
    </div>
  );
};
